import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, addDoc, onSnapshot, query, orderBy, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FaPen, FaTimes, FaPaperPlane, FaTags, FaLock } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLoader } from 'react-icons/fi';

function ForumPostingArea({ onPostCreated }) {
  const { currentUser } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState({
    isPremium: false,
    isAdmin: false,
    isLoading: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    // Check user's premium and admin status
    const checkUserStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserStatus({
            isPremium: userData.membershipLevel === 'premium' || userData.membershipLevel === 'promo' || userData.isPremium || false,
            isAdmin: userData.isAdmin || false,
            isLoading: false
          });
        } else {
          setUserStatus(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Error checking user status:', error);
        setUserStatus(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkUserStatus();

    // Set up labels listener
    const labelsQuery = query(
      collection(db, 'forumLabels'),
      orderBy('sorting', 'asc'),
      orderBy('labelName')
    );

    const unsubscribe = onSnapshot(labelsQuery, (snapshot) => {
      const labels = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAvailableLabels(labels);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching labels:', error);
      toast.error('Failed to load labels');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    // Validate inputs
    if (title.trim().length < 5) {
      toast.error('Title must be at least 5 characters long');
      return;
    }

    if (content.trim().length < 10) {
      toast.error('Content must be at least 10 characters long');
      return;
    }

    // Validate that at least one label is selected
    if (selectedLabels.length === 0) {
      toast.error('Please select at least one label for your post');
      return;
    }

    try {
      setSubmitting(true);

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }

      const userData = userDoc.data();

      // Check if user is admin or premium
      if (!userData.isAdmin && !(userData.isPremium || userData.membershipLevel === 'premium' || userData.membershipLevel === 'promo')) {
        toast.error('Only premium users can create posts');
        return;
      }

      const postData = {
        postTitle: title,
        postContent: content,
        postLabels: selectedLabels,
        userId: currentUser.uid,
        username: userData.username,
        isAdmin: userData.isAdmin,
        createdAt: serverTimestamp(),
        upvoteCount: 0,
        downvoteCount: 0,
        postStatus: 'active',
        reportedBy: [],
        reportStatus: null,
        lastEditedAt: serverTimestamp(),
        upvotedBy: [],
        downvotedBy: []
      };

      await addDoc(collection(db, 'posts'), postData);
      
      toast.success('Post created successfully!');
      setTitle('');
      setContent('');
      setSelectedLabels([]);
      setIsExpanded(false);
      if (typeof onPostCreated === 'function') {
        onPostCreated();
      }
      
      // Scroll to the top of the posts
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error creating post:', error);
      if (error.code === 'permission-denied') {
        toast.error('You do not have permission to create posts');
      } else {
        toast.error('Failed to create post');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLabel = (label) => {
    setSelectedLabels(prev =>
      prev.includes(label.labelId)
        ? prev.filter(id => id !== label.labelId)
        : [...prev, label.labelId]
    );
  };

  if (!isExpanded) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="forum-posting-area bg-gradient-to-r from-teal-500 to-indigo-600 dark:from-teal-700 dark:to-indigo-800 rounded-xl shadow-lg p-4 mb-8"
      >
        {userStatus.isLoading ? (
          <div className="flex items-center justify-center h-12 bg-white bg-opacity-20 dark:bg-opacity-10 backdrop-blur-sm rounded-lg">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white"></div>
            <span className="ml-3 text-white">Checking account status...</span>
          </div>
        ) : !userStatus.isPremium && !userStatus.isAdmin ? (
          <div 
            className="flex items-center justify-between p-4 bg-white bg-opacity-20 dark:bg-opacity-10 backdrop-blur-sm rounded-lg"
          >
            <div className="flex items-center">
              <FaLock className="text-white mr-3" />
              <p className="text-white font-medium">
                Premium feature: Create posts and start discussions
              </p>
            </div>
            <button
              className="px-4 py-2 bg-white text-purple-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Upgrade to Premium
            </button>
          </div>
        ) : (
          <div 
            onClick={() => setIsExpanded(true)}
            className="flex items-center p-4 bg-white bg-opacity-20 dark:bg-opacity-10 backdrop-blur-sm rounded-lg cursor-pointer hover:bg-opacity-30 transition-all"
          >
            <FaPen className="text-teal-900 dark:text-white mr-3" />
            <p className="text-teal-900 dark:text-white font-medium">
              Share something with the community...
            </p>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div 
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="forum-posting-area bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden"
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create a Post</h3>
          <button
            onClick={() => {
              setIsExpanded(false);
              setTitle('');
              setContent('');
              setSelectedLabels([]);
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <FaTimes className="text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a descriptive title"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
              minLength={5}
              maxLength={40}
            />
            <div className="text-xs text-gray-500 mt-1">
              {title.length}/40 characters (minimum 5)
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Content
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What's on your mind?"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows="6"
              required
              minLength={10}
            />
            <div className="text-xs text-gray-500 mt-1">
              {content.length}/2000 characters (minimum 10)
            </div>
          </div>

          {/* Labels Selection */}
          <div className="mb-6">
            <div className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FaTags className="mr-2" />
              <span>Labels (required)</span>
            </div>
            {loading ? (
              <div className="flex justify-center py-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableLabels.map(label => (
                  <button
                    key={label.id}
                    type="button"
                    onClick={() => toggleLabel(label)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      selectedLabels.includes(label.labelId)
                        ? 'bg-violet-200 text-violet-700 border-violet-200'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {label.labelName}
                  </button>
                ))}
              </div>
            )}
            {selectedLabels.length === 0 && (
              <p className="mt-2 text-xs text-red-500">Please select at least one label</p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setIsExpanded(false);
                setTitle('');
                setContent('');
                setSelectedLabels([]);
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 shadow-md disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <FiLoader className="animate-spin" />&nbsp;
                  <span className="mr-2">Posting...</span>
                </>
              ) : (
                <>
                  <FaPaperPlane className="mr-2" />
                  <span>Post</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </AnimatePresence>
  );
}

export default ForumPostingArea;
